/**
 * FundraiserSettings.tsx - Application Settings and Configuration Interface
 *
 * This component provides a settings interface for configuring application-wide
 * preferences including OpenAI integration, API keys, and other system settings.
 *
 * Key Features:
 * - OpenAI API configuration and model selection
 * - API key management with validation
 * - Settings persistence to localStorage
 * - Model availability checking and filtering
 * - Form validation and error handling
 * - Responsive settings interface
 *
 * Settings Categories:
 * - AI Integration: OpenAI API keys and model selection
 * - Image Generation: Model preferences and settings
 * - Text Generation: Model selection and parameters
 *
 * Props: None (manages its own state and localStorage)
 *
 * Dependencies: MobX store, OpenAI service, styled-components
 */

import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { ArrowLeft, Save, Key, Brain, Image as ImageIcon } from "lucide-react";
import { fundraiserStore } from "../stores/FundraiserStore";
// OpenAI service will be loaded dynamically when needed
import { Toast } from "./Toast";
import { OpenAIService } from "../utils/openaiService";
import { NavigateFunction } from "react-router-dom";
import {
  AuthService,
  Loader,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import {
  ISaveOpenAISettingsRequestModel,
  IUpdateOpenAISettingsRequestModel,
} from "../interfaces";
import { firstValueFrom } from "rxjs";
import { FundraiserService } from "../services";
import { OpenAISettingsModel } from "../models";
import ToastService from "./ToastService";

const SettingsContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(
    135deg,
    #667eea 0%,
    #764ba2 25%,
    #f093fb 50%,
    #f5576c 75%,
    #4facfe 100%
  );
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.9) 0%,
      rgba(248, 250, 252, 0.95) 100%
    );
    pointer-events: none;
  }
`;

const HeaderContainer = styled.div<{ isScrolled: boolean }>`
  position: sticky;
  top: 0;
  z-index: 100;
  background: ${(props) =>
    props.isScrolled
      ? "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.95) 100%)"
      : "transparent"};
  backdrop-filter: ${(props) => (props.isScrolled ? "blur(20px)" : "none")};
  border-bottom: ${(props) =>
    props.isScrolled ? "1px solid rgba(255, 255, 255, 0.3)" : "none"};
  box-shadow: ${(props) =>
    props.isScrolled ? "0 4px 20px rgba(0, 0, 0, 0.1)" : "none"};
  padding: ${(props) => (props.isScrolled ? "16px 0" : "32px 0")};
  transition: all 0.3s ease;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 32px;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  padding: 0 32px 80px;
  min-height: 100vh;
  max-width: 1400px;
  margin: 0 auto;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  &:hover {
    background: rgba(255, 255, 255, 1);
    transform: translateY(-1px);
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const ActionButton = styled.button<{ variant?: "primary" }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  backdrop-filter: blur(12px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);

  ${(props) =>
    props.variant === "primary"
      ? `
    background: linear-gradient(135deg, #10b981, #059669);
    color: #ffffff;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(16, 185, 129, 0.4);
    }
  `
      : `
    background: rgba(255, 255, 255, 0.9);
    color: #64748b;
    border: 1px solid rgba(255, 255, 255, 0.3);

    &:hover {
      background: rgba(255, 255, 255, 1);
      color: #1e293b;
      transform: translateY(-1px);
    }
  `}

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const FormCard = styled.div`
  position: relative;
  z-index: 2;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 40px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.3);
  margin-bottom: 32px;
  margin-top: 15px;
`;

const Section = styled.div`
  margin-bottom: 40px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
`;

const Label = styled.label`
  font-size: 16px;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const PasswordInput = styled.input`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  background: #ffffff;
  font-family: monospace;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
    font-family: inherit;
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    background: #f8fafc;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const HelpText = styled.p`
  font-size: 14px;
  color: #64748b;
  margin-top: 8px;
  line-height: 1.4;
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid #e2e8f0;
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const StatusIndicator = styled.div<{ status: "success" | "error" | "loading" }>`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  font-size: 14px;
  font-weight: 500;

  ${(props) => {
    switch (props.status) {
      case "success":
        return "color: #10b981;";
      case "error":
        return "color: #ef4444;";
      case "loading":
        return "color: #3b82f6;";
      default:
        return "color: #64748b;";
    }
  }}
`;

type IFundraiserSettingsProps = {
  navigate: NavigateFunction;
  aiSettings: OpenAISettingsModel;
};

const FundraiserSettings = observer(
  ({ navigate, aiSettings }: IFundraiserSettingsProps) => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [availableModels, setAvailableModels] = useState<{
      textModels: string[];
      imageModels: string[];
    }>({
      textModels: ["gpt-4o", "gpt-4-turbo", "gpt-4"],
      imageModels: ["dall-e-3", "gpt-image-1"],
    });
    const [modelsStatus, setModelsStatus] = useState<
      "success" | "error" | "loading" | null
    >(null);
    const [showLoader, setShowLoader] = useState(false);

    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 50);
    };

    useEffect(() => {
      window.addEventListener("scroll", handleScroll, { passive: true });
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }, []);

    const handleBackClick = () => {
      navigate("/fundraiser/dashboard");
    };

    const handleSave = async () => {
      if (isSaving) return;
      setIsSaving(true);

      const isUpdate = aiSettings !== null && aiSettings.id !== "";

      try {
        if (isUpdate) {
          const updateRequest = aiSettings.createUpdateRequestModel(
            AuthService.authData.accountId
          );
          let response = await firstValueFrom(
            FundraiserService.updateOpenAISettings(updateRequest)
          );

          if (response?.result) {
            ToastService.showSuccess("OpenAI settings updated successfully");
          } else {
            ToastService.showError("Failed to update OpenAI settings");
          }
        } else {
          const saveRequest = aiSettings.createSaveRequestModel(
            AuthService.authData.accountId
          );

          let response = await firstValueFrom(
            FundraiserService.saveOpenAISettings(saveRequest)
          );

          if (response?.result) {
            fundraiserStore.setOpenAISettings(
              new OpenAISettingsModel({
                ...aiSettings,
                id: response.result,
              })
            );

            ToastService.showSuccess("OpenAI settings saved successfully");
          } else {
            ToastService.showError("Failed to save OpenAI settings");
          }
        }
      } finally {
        setIsSaving(false);
      }
    };

    const handleInputChange = (
      field: keyof OpenAISettingsModel,
      value: string
    ) => {
      fundraiserStore.setOpenAISettings(
        new OpenAISettingsModel({
          ...aiSettings,
          [field]: value,
        })
      );
    };

    const getModelsStatusText = () => {
      switch (modelsStatus) {
        case "loading":
          return "Loading available models...";
        case "success":
          return `Loaded ${availableModels.textModels.length} text and ${availableModels.imageModels.length} image models`;
        case "error":
          return "Failed to load models. Please check your API key.";
        default:
          return "";
      }
    };

    // // Load available models when API key changes
    // useEffect(() => {
    //   if (apiKey && apiKey.trim() !== "") {
    //     loadAvailableModels();
    //   }
    // }, [apiKey]);

    // const loadAvailableModels = async () => {
    //   if (!apiKey || apiKey.trim() === "") return;

    //   setIsLoadingModels(true);
    //   setModelsStatus("loading");

    //   try {
    //     const { OpenAIService } = await import("../utils/openaiService");

    //     // Save API key first to make it available for the API call
    //     const tempSettings = { apiKey, textModel, imageModel };
    //     OpenAIService.saveSettings(tempSettings);

    //     const models = await OpenAIService.fetchAvailableModels();
    //     setAvailableModels(models);
    //     setModelsStatus("success");
    //   } catch (error) {
    //     console.error("Error loading models:", error);
    //     setModelsStatus("error");
    //     setToastMessage("Failed to load models. Please check your API key.");
    //     setShowToast(true);
    //   } finally {
    //     setIsLoadingModels(false);
    //   }
    // };

    if (showLoader) {
      return <>{new Loader(true).loaderComponent}</>;
    }
    return (
      <SettingsContainer>
        <HeaderContainer isScrolled={isScrolled}>
          <Header>
            <LeftSection>
              <BackButton onClick={handleBackClick}>
                <ArrowLeft size={20} />
              </BackButton>
              <Title>Settings</Title>
            </LeftSection>

            <ActionButtons>
              <ActionButton
                variant="primary"
                onClick={handleSave}
                disabled={isSaving}
              >
                <Save size={18} />
                {isSaving ? "Saving..." : "Save Settings"}
              </ActionButton>
            </ActionButtons>
          </Header>
        </HeaderContainer>

        <ContentWrapper>
          <FormCard>
            <Section>
              <SectionTitle>
                <Key size={20} />
                Open AI Integration
              </SectionTitle>

              <FormGroup>
                <Label htmlFor="apiKey">API Key</Label>
                <PasswordInput
                  id="apiKey"
                  type="password"
                  placeholder="Enter your OpenAI API key..."
                  value={aiSettings?.apiKey || ""}
                  onChange={(e) => handleInputChange("apiKey", e.target.value)}
                />
                <HelpText>
                  Your OpenAI API key is stored locally and used for AI image
                  editing and description generation. Get your API key from{" "}
                  <a
                    href="https://platform.openai.com/api-keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#3b82f6" }}
                  >
                    OpenAI Platform
                  </a>
                  .
                </HelpText>
                {modelsStatus && (
                  <StatusIndicator status={modelsStatus}>
                    {modelsStatus === "loading" && <LoadingSpinner />}
                    {getModelsStatusText()}
                  </StatusIndicator>
                )}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="textModel">
                  <Brain size={16} style={{ marginRight: "5px" }} />
                  Text Model
                </Label>
                <Select
                  style={{ width: "30%" }}
                  id="textModel"
                  value={aiSettings?.textModel || ""}
                  onChange={(e) =>
                    handleInputChange("textModel", e.target.value)
                  }
                  disabled={availableModels.textModels.length === 0}
                >
                  {availableModels.textModels.length > 0 ? (
                    availableModels.textModels.map((model) => (
                      <option key={model} value={model}>
                        {model}
                      </option>
                    ))
                  ) : (
                    <option value={aiSettings?.textModel || ""}>
                      {aiSettings?.textModel || ""}
                    </option>
                  )}
                </Select>
                <HelpText>
                  Model used for generating image descriptions and text-based AI
                  features.
                </HelpText>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="imageModel">
                  <ImageIcon size={16} style={{ marginRight: "5px" }} />
                  Image Model
                </Label>
                <Select
                  style={{ width: "30%" }}
                  id="imageModel"
                  value={aiSettings?.imageModel || ""}
                  onChange={(e) =>
                    handleInputChange("imageModel", e.target.value)
                  }
                  disabled={availableModels.imageModels.length === 0}
                >
                  {availableModels.imageModels.length > 0 ? (
                    availableModels.imageModels.map((model) => (
                      <option key={model} value={model}>
                        {model}
                      </option>
                    ))
                  ) : (
                    <option value={aiSettings?.imageModel || ""}>
                      {aiSettings?.imageModel || ""}
                    </option>
                  )}
                </Select>
                <HelpText>
                  Model used for AI image editing, generation, and background
                  removal features.
                </HelpText>
              </FormGroup>
            </Section>
          </FormCard>
        </ContentWrapper>
      </SettingsContainer>
    );
  }
);

export default withRouter(FundraiserSettings);
